import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { withOrientationChange } from 'react-device-detect';

import { useSelector, useDispatch } from 'react-redux';
import * as DataActions from '../../redux/actions/data';

import FilterList from '../../components/FilterList';
import ManyStatus from '../../components/Many-status';
import Segment from '../../components/Segment';
import FiltersHeader from '../../components/filtersHeader';
import Gap from '../../components/Gap';

import {
  ModalFilter,
  SummaryData,
  Status as StatusInterface,
  SummarySales,
  SummaryShare,
  SummaryDCP,
  SummaryMedia,
  SummaryShelf,
  SummaryNetSales,
  Segment as SegmentType
} from '../../types';
import { ReduxStates } from '../../redux/reduxTypes';

import {
  fetchNetSales,
  fetchSales,
  fetchMedia,
  fetchShare,
  fetchShareCompetitive,
  hasAllNullProperties,
  fetchDcp,
  isEmpty,
  fetchSpgr
} from './helpers';
import { cardContainerStyles, cardTitleStyles, segmentNotificationStyles } from './configs';
import { formatDateFilter, returnFormattedSegmentFilters } from '../../helpers/format';
import moment from 'moment';
import DigitalShelf from './components/DigitalShelf';
import { useOktaAuth } from '@okta/okta-react';

let Overview = (): any => {
  const filters = useSelector((state: ReduxStates) => state.filters);
  // const savedFilters = useSelector((state: ReduxStates) => state.saved);
  const data = useSelector((state: ReduxStates) => state.data);
  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const user = useSelector((state: ReduxStates) => state.user);
  const client = useSelector((state: ReduxStates) => state.client);

  const history = useHistory();
  const dispatch = useDispatch();

  const [dateFilter, setDateFilter] = useState<string>(filters?.dates?.range || '');
  const [customRangeFilter, setCustomRangeFilter] = useState<Array<number | string> | null>(filters.dates?.custom);
  // const [marketFilters, setMarketFilters] = useState<Array<ModalFilter | null>>(savedFilters.savedMarket);
  const [marketFilters, setMarketFilters] = useState<Array<ModalFilter | null>>([]);
  const [status, setStatus] = useState<StatusInterface[]>([]);
  const selectedCurrency = data.selectedCurrency;

  const [overviewData, setOverviewData] = useState<SummaryData | null>(data.overviewData);
  const [overviewSalesData, setOverviewSalesData] = useState<SummarySales | null>(data.overviewData?.sales || null);
  const [overviewNetSalesData, setOverviewNetSalesData] = useState<SummaryNetSales | null>(
    data.overviewData?.netSales || null
  );
  const [overviewShareData, setOverviewShareData] = useState<SummaryShare | null>(data.overviewData?.share || null);
  const [overviewShareCompetitiveData, setOverviewShareCompetitiveData] = useState<SummaryShare | null>(
    data.overviewData?.shareCompetitive || null
  );
  const [overviewDcpData, setOverviewDcpData] = useState<SummaryDCP | null>(data.overviewData?.dcp || null);
  const [overviewMediaData, setOverviewMediaData] = useState<SummaryMedia | null>(data.overviewData?.media || null);
  const [spgrData, setSpgrData] = useState<SegmentType | null>(data.overviewData?.spgr || null);
  const [overviewShelfData] = useState<SummaryShelf | null>(data.overviewData?.shelf || null);

  const [fetchParams, setFetchParams] = useState<any | null>(null);

  const [showPlanPercentForNetSales, setShowPlanPercentForNetSales] = useState<boolean>(true);
  const [isShare, setIsShare] = useState<boolean>(true);
  const [fetchedMessagingInfo, setFetchedMessagingInfo] = useState<boolean>(false);
  const [isDigitalShelf, setIsDigitalShelf] = useState<boolean>(true);
  const [showWidgets, setShowWidgets] = useState<boolean>(true);

  const { oktaAuth } = useOktaAuth();

  useEffect(() => {
    if (data.messagingInfo) {
      const isShare = data.messagingInfo.filter((option) => option.dataset === 'Share').at(0)?.status;
      const isDigitalShelf = data.messagingInfo.filter((option) => option.dataset === 'DigitalShelf').at(0)?.status;
      isShare !== undefined && setIsShare(isShare);
      isDigitalShelf !== undefined && setIsDigitalShelf(isDigitalShelf);
      setFetchedMessagingInfo(true);
    }
  }, [data.messagingInfo]);

  // set document title
  document.title = 'Overview - Ascential Connect';

  //reroute if not in userModules
  useEffect(() => {
    if (user && user.userModules && !user.userModules.includes('Overview')) history.push('/glance');
  }, [user]);

  const handleClose = (closed: StatusInterface) => {
    const index = status.indexOf(closed);
    if (index > -1) {
      status.splice(index, 1);
      setStatus([...status]);
    }
  };

  //set market filters from filters state
  useEffect(() => {
    setMarketFilters(filters.market);
    const marketFilters = [...filters.market];
    const filterNames = marketFilters.map((x) => x?.name);
    if (filterNames.includes('Category') || filterNames.includes('Brand')) {
      setShowPlanPercentForNetSales(false);
    } else {
      setShowPlanPercentForNetSales(true);
    }
    if (
      filters.market?.some(
        (value) =>
          value?.values?.some((value) => value.name.toLowerCase().includes('amazon')) && value?.values?.length === 1
      )
    ) {
      setShowWidgets(true);
    } else {
      setShowWidgets(false);
    }
  }, [filters.market]);

  // fetch overview data
  useEffect(() => {
    if (selectedCurrency && marketFilters.length && copyData && data.dateFilterRangeLimit) {
      const segmentFilters = returnFormattedSegmentFilters(marketFilters, true);
      const dateFilters = formatDateFilter(
        dateFilter,
        customRangeFilter || undefined,
        copyData,
        data.dateFilterRangeLimit
      );
      const formattedDates = dateFilters.split(' - ');
      const startDate = moment(new Date(formattedDates[0])).format('YYYY-MM-DD');
      const endDate = moment(new Date(formattedDates[1])).format('YYYY-MM-DD');
      const params = {
        ...segmentFilters,
        // sets date to ISO format YYYY-MM-DD (refer to OpenAPI specs)
        startDate,
        endDate
      };
      const ytdParams = {
        ...segmentFilters,
        // sets date to ISO format YYYY-MM-DD (refer to OpenAPI specs)
        startDate,
        endDate,
        maxAvailableDate: data.dateFilterRangeLimit[0].endDate
      };

      setFetchParams(params);

      // reset state(s)
      setOverviewNetSalesData(null);
      setOverviewSalesData(null);
      setOverviewShareData(null);
      setOverviewShareCompetitiveData(null);
      setOverviewDcpData(null);
      setOverviewMediaData(null);
      setSpgrData(null);

      const fetchSalesData = async () => {
        try {
          const netSalesData = await fetchNetSales(params);
          const parsedNetSalesData: SummaryNetSales = {
            netSales: netSalesData
          };
          setOverviewNetSalesData(parsedNetSalesData);
        } catch (error: any) {
          setStatus([...status, { state: 'error', message: error }]);
        }
        try {
          const salesData = await fetchSales(ytdParams);
          setOverviewSalesData(salesData);
        } catch (error: any) {
          setStatus([...status, { state: 'error', message: error }]);
        }
      };
      const fetchShareData = async () => {
        try {
          const shareData = await fetchShare(params);
          setOverviewShareData(shareData);
        } catch (error: any) {
          setStatus([...status, { state: 'error', message: error }]);
        }
        try {
          const shareCompetitiveData = await fetchShareCompetitive(params);
          setOverviewShareCompetitiveData(shareCompetitiveData);
        } catch (error: any) {
          setStatus([...status, { state: 'error', message: error }]);
        }
      };
      const fetchDcpData = async () => {
        try {
          const dcpData = await fetchDcp(params);
          setOverviewDcpData(dcpData);
        } catch (error: any) {
          setStatus([...status, { state: 'error', message: error }]);
        }
      };
      const fetchMediaData = async () => {
        try {
          const mediaData = await fetchMedia(ytdParams, oktaAuth);

          setOverviewMediaData(mediaData);
        } catch (error: any) {
          setStatus([...status, { state: 'error', message: error }]);
        }
      };
      const fetchSpgrData = async () => {
        try {
          const spgrData = await fetchSpgr(ytdParams);

          setSpgrData(spgrData.spgr);
        } catch (error: any) {
          setStatus([...status, { state: 'error', message: error }]);
        }
      };
      if (fetchedMessagingInfo) {
        fetchSalesData();
        if (isShare && showWidgets) {
          fetchShareData();
        }
        if (showWidgets) {
          fetchDcpData();
          fetchSpgrData();
        }
        fetchMediaData();
      }
    }
  }, [
    marketFilters,
    selectedCurrency,
    dateFilter,
    customRangeFilter,
    copyData,
    fetchedMessagingInfo,
    data.dateFilterRangeLimit
  ]);

  // update redux value(s) based on one ore more local state changes to data
  useEffect(() => {
    const data: SummaryData = {
      sales: overviewSalesData,
      share: overviewShareData,
      shareCompetitive: overviewShareCompetitiveData,
      dcp: overviewDcpData,
      media: overviewMediaData,
      shelf: overviewShelfData,
      netSales: overviewNetSalesData,
      spgr: spgrData
    };
    if (client === 'demo' && data?.media?.spgr) {
      data.media.spgr.currentValue = 8.25;
      data.media.spgr.momPercentage = -22.58;
      data.media.spgr.yoyPercentage = 17.81;
      data.media.spgr.ytdPercentage = 24.82;
      data.media.spgr.currentValueType = 'percentage';
    }
    dispatch(DataActions.setOverviewData(data));
    setOverviewData(data);
  }, [
    overviewSalesData,
    overviewShareData,
    overviewShareCompetitiveData,
    overviewDcpData,
    overviewMediaData,
    overviewShelfData,
    overviewNetSalesData,
    spgrData
  ]);

  return (
    <div className="relative pt-[30px] pb-[100px] px-6">
      <ManyStatus status={status} onExit={handleClose} />
      {/* GLOBAL FILTERS */}
      <FiltersHeader
        marketFilters={marketFilters}
        status={status}
        dateFilter={dateFilter}
        customRangeFilter={customRangeFilter}
        setDateFilter={setDateFilter}
        setCustomRangeFilter={setCustomRangeFilter}
      />
      <div id="filters_breadcrumbs" className="w-full mt-3 mx-0 mb-5">
        <FilterList filters={marketFilters} />
      </div>
      {/* MAIN BODY */}
      <div className="block">
        <div className={cardContainerStyles('main')}>
          <p className={cardTitleStyles}>{copyData?.overview_sales_title}</p>
          <div className={`grid grid-cols-5 mt-2.5 w-full max-w-full`}>
            {overviewData?.sales && overviewData?.netSales ? (
              <>
                <Segment
                  onClick={() => history.push({ pathname: '/overview/sales', state: { index: 0 } })}
                  currency={overviewData.netSales.netSales?.currencyCode}
                  title={copyData?.segment_title_net}
                  totalValue={overviewData.netSales.netSales?.currentValue}
                  YoY={overviewData.netSales.netSales?.yoyPercentageChange}
                  MoM={overviewData.netSales.netSales?.momPercentageChange}
                  target={overviewData.netSales.netSales?.planPercentageChange}
                  displayData={showPlanPercentForNetSales ? ['YoY', 'MoM', 'target'] : ['YoY', 'MoM']}
                  screen="overview"
                />
                {showWidgets ? (
                  <>
                    <Segment
                      onClick={() => history.push({ pathname: '/overview/sales', state: { index: 1 } })}
                      currency={overviewData.sales.orderedRevenue?.currencyCode}
                      title={copyData?.segment_title_pos}
                      totalValue={overviewData.sales.orderedRevenue?.currentValue}
                      YoY={overviewData.sales.orderedRevenue?.yoyPercentage}
                      MoM={overviewData.sales.orderedRevenue?.momPercentage}
                      YtD={overviewData.sales.orderedRevenue?.ytdPercentage}
                      target={overviewData.sales.orderedRevenue?.targetPercentage}
                      displayData={['YoY', 'MoM', 'YtD']}
                      screen="overview"
                      tooltipData={{ status: 'PARTIAL', message: copyData.pos_tooltip }}
                    />
                    <Segment
                      onClick={() => history.push({ pathname: '/overview/sales', state: { index: 2 } })}
                      currency={overviewData.sales.glanceViews?.currencyCode}
                      title={copyData?.segment_title_glance_views}
                      totalValue={overviewData.sales.glanceViews?.currentValue}
                      YoY={overviewData.sales.glanceViews?.yoyPercentage}
                      MoM={overviewData.sales.glanceViews?.momPercentage}
                      YtD={overviewData.sales.glanceViews?.ytdPercentage}
                      target={overviewData.sales.glanceViews?.targetPercentage}
                      displayData={['YoY', 'MoM', 'YtD']}
                      screen="overview"
                    />
                    <Segment
                      onClick={() => history.push({ pathname: '/overview/sales', state: { index: 3 } })}
                      currency={overviewData.sales.conversionRate?.currencyCode}
                      title={copyData?.segment_title_conversion_rate}
                      totalValue={overviewData.sales.conversionRate?.currentValue}
                      YoY={overviewData.sales.conversionRate?.yoyPercentage}
                      MoM={overviewData.sales.conversionRate?.momPercentage}
                      YtD={overviewData.sales.conversionRate?.ytdPercentage}
                      target={overviewData.sales.conversionRate?.targetPercentage}
                      displayData={['YoY', 'MoM', 'YtD']}
                      unit={'%'}
                      screen="overview"
                    />
                    <Segment
                      onClick={() => history.push({ pathname: '/overview/sales', state: { index: 4 } })}
                      currency={overviewData.sales.asp?.currencyCode}
                      title={copyData?.segment_title_asp}
                      totalValue={overviewData.sales.asp?.currentValue}
                      YoY={overviewData.sales.asp?.yoyPercentage}
                      MoM={overviewData.sales.asp?.momPercentage}
                      YtD={overviewData.sales.asp?.ytdPercentage}
                      target={overviewData.sales.asp?.targetPercentage}
                      displayData={['YoY', 'MoM', 'YtD']}
                      screen="overview"
                    />
                  </>
                ) : (
                  ''
                )}
              </>
            ) : (
              <p className={segmentNotificationStyles}>{copyData?.loading}</p>
            )}
          </div>
        </div>
        {showWidgets ? (
          <div className="flex w-full max-w-full">
            <div className={`${cardContainerStyles('main')}`}>
              <p className={cardTitleStyles}>{copyData?.overview_share_title}</p>
              <div className={`grid grid-cols-2 mt-2.5 w-full max-w-full`}>
                {overviewData?.share && overviewData?.shareCompetitive && isShare ? (
                  !hasAllNullProperties(overviewData?.share) &&
                  !hasAllNullProperties(overviewData?.shareCompetitive) ? (
                    <>
                      <Segment
                        onClick={() => history.push({ pathname: '/overview/share', state: { index: 0 } })}
                        currency={overviewData.share.amazonShare?.currencyCode}
                        title={copyData?.segment_title_amazon}
                        unit="%"
                        totalValue={overviewData.share.amazonShare?.currentValue}
                        MoM={overviewData.share.amazonShare?.momPercentage}
                        last13={overviewData.share.amazonShare?.l13wPercentage}
                        last52={overviewData.share.amazonShare?.l52wPercentage}
                        displayData={['MoM', 'last13', 'last52']}
                        screen="overview"
                      />
                      <Segment
                        onClick={() => history.push({ pathname: '/overview/share', state: { index: 1 } })}
                        currency={overviewData.shareCompetitive.amazonShare?.currencyCode}
                        title={copyData?.segment_title_competitive_sales}
                        totalValue={overviewData.shareCompetitive.amazonShare?.currentValue}
                        MoM={overviewData.shareCompetitive.amazonShare?.momPercentage}
                        last13={overviewData.shareCompetitive.amazonShare?.l13wPercentage}
                        last52={overviewData.shareCompetitive.amazonShare?.l52wPercentage}
                        displayData={['MoM', 'last13', 'last52']}
                        screen="overview"
                      />
                    </>
                  ) : (
                    <p className={segmentNotificationStyles}>{copyData?.no_data}</p>
                  )
                ) : (
                  <div>
                    {isShare ? (
                      <p className={segmentNotificationStyles}>{copyData?.loading}</p>
                    ) : (
                      <p className={`${segmentNotificationStyles} w-full text-center p-10`}>
                        {copyData?.no_api_data}
                        <a
                          className="underline text-blue-500"
                          href={'https://www.ascentialedge.com/demo-request3'}
                          target="_blank"
                        >
                          {copyData?.talk_to_expert}
                        </a>
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <Gap padding="8px 13px" />
            <div className={`${cardContainerStyles('main')}`}>
              <p className={cardTitleStyles}>{copyData?.overview_dcp_title}</p>
              {overviewData?.dcp ? (
                !hasAllNullProperties(overviewData?.dcp) ? (
                  <>
                    <div className={`grid grid-cols-2 mt-2.5 w-full max-w-full`}>
                      <Segment
                        onClick={() =>
                          history.push({ pathname: '/overview/digital-commerce-penetration', state: { index: 0 } })
                        }
                        title={copyData?.segment_title_dcp}
                        variant="wideTitle"
                        unit="%"
                        totalValue={overviewData.dcp.penetration.currentValue}
                        MoM={overviewData.dcp.penetration.momPercentage}
                        YoY={overviewData.dcp.penetration.yoyPercentage}
                        displayData={['MoM', 'YoY']}
                        tooltipData={{ status: 'PARTIAL', message: copyData.netsales_tooltip }}
                        screen="overview"
                      />
                    </div>
                  </>
                ) : (
                  <p className={segmentNotificationStyles}>{copyData?.no_data}</p>
                )
              ) : (
                <p className={segmentNotificationStyles}>{copyData?.loading}</p>
              )}
            </div>
          </div>
        ) : (
          ''
        )}
        <div className={cardContainerStyles('main')}>
          <p className={cardTitleStyles}>{copyData?.overview_media_title}</p>
          <div className={`grid grid-cols-5 mt-2.5 w-full max-w-full`}>
            {overviewData?.media ? (
              (!hasAllNullProperties(overviewData?.media) &&
                overviewData?.media?.amazonSummary &&
                !isEmpty(overviewData?.media?.amazonSummary)) ||
              overviewData?.media?.spgr ? (
                <>
                  {overviewData && showWidgets && (
                    <Segment
                      onClick={() => history.push({ pathname: '/overview/media', state: { index: 0 } })}
                      currency={overviewData?.spgr?.currencyCode}
                      title={copyData?.segment_title_spgr}
                      unit="%"
                      totalValue={overviewData.spgr?.currentValue}
                      MoM={overviewData.spgr?.momPercentage}
                      YoY={overviewData.spgr?.yoyPercentage}
                      YtD={overviewData.spgr?.ytdPercentage}
                      target={overviewData.spgr?.targetPercentage}
                      displayData={['MoM', 'YoY', 'YtD']}
                      screen="overview"
                    />
                  )}
                  {overviewData?.media?.amazonSummary && (
                    <>
                      {overviewData.media.amazonSummary?.clicks && (
                        <Segment
                          onClick={() => history.push({ pathname: '/overview/media', state: { index: 1 } })}
                          currency={overviewData.media.amazonSummary.clicks.currencyCode}
                          title={copyData?.segment_title_clicks}
                          totalValue={overviewData.media.amazonSummary?.clicks?.currentValue}
                          MoM={overviewData.media.amazonSummary?.clicks?.momPercentage}
                          YoY={overviewData.media.amazonSummary?.clicks?.yoyPercentage}
                          YtD={overviewData.media.amazonSummary?.clicks?.ytdPercentage}
                          target={overviewData.media.amazonSummary?.clicks?.targetPercentage}
                          displayData={['MoM', 'YoY', 'YtD']}
                          screen="overview"
                        />
                      )}
                      {overviewData.media.amazonSummary?.conversions && (
                        <Segment
                          onClick={() => history.push({ pathname: '/overview/media', state: { index: 2 } })}
                          currency={overviewData.media.amazonSummary.conversions.currencyCode}
                          title={copyData?.segment_title_conversations}
                          totalValue={overviewData.media.amazonSummary?.conversions?.currentValue}
                          MoM={overviewData.media.amazonSummary?.conversions?.momPercentage}
                          YoY={overviewData.media.amazonSummary?.conversions?.yoyPercentage}
                          YtD={overviewData.media.amazonSummary?.conversions?.ytdPercentage}
                          target={overviewData.media.amazonSummary?.conversions?.targetPercentage}
                          displayData={['MoM', 'YoY', 'YtD']}
                          screen="overview"
                        />
                      )}
                      {overviewData.media.amazonSummary?.cpa && (
                        <Segment
                          onClick={() => history.push({ pathname: '/overview/media', state: { index: 3 } })}
                          currency={overviewData.media.amazonSummary.cpa.currencyCode}
                          title={copyData?.segment_title_cpa}
                          totalValue={overviewData.media.amazonSummary?.cpa?.currentValue}
                          MoM={overviewData.media.amazonSummary?.cpa?.momPercentage}
                          YoY={overviewData.media.amazonSummary?.cpa?.yoyPercentage}
                          YtD={overviewData.media.amazonSummary?.cpa?.ytdPercentage}
                          target={overviewData.media.amazonSummary?.cpa?.targetPercentage}
                          displayData={['MoM', 'YoY', 'YtD']}
                          screen="overview"
                        />
                      )}
                      {overviewData.media.amazonSummary?.aov && (
                        <Segment
                          onClick={() => history.push({ pathname: '/overview/media', state: { index: 4 } })}
                          currency={overviewData.media.amazonSummary.aov.currencyCode}
                          title={copyData?.segment_title_aov}
                          totalValue={overviewData.media.amazonSummary?.aov?.currentValue}
                          MoM={overviewData.media.amazonSummary?.aov?.momPercentage}
                          YoY={overviewData.media.amazonSummary?.aov?.yoyPercentage}
                          YtD={overviewData.media.amazonSummary?.aov?.ytdPercentage}
                          target={overviewData.media.amazonSummary?.aov?.targetPercentage}
                          displayData={['MoM', 'YoY', 'YtD']}
                          tooltipData={{
                            status: 'PARTIAL',
                            message: showWidgets ? copyData.aov_ams_tooltip : copyData.aov_non_ams_tooltip
                          }}
                          screen="overview"
                        />
                      )}
                    </>
                  )}
                </>
              ) : (
                <p className={segmentNotificationStyles}>{copyData?.no_data}</p>
              )
            ) : (
              <p className={segmentNotificationStyles}>{copyData?.loading}</p>
            )}
          </div>
        </div>

        <div className={cardContainerStyles('main')}>
          <DigitalShelf copyData={copyData} params={fetchParams} isDigitalShelf={isDigitalShelf} />
        </div>
      </div>
    </div>
  );
};

// @ts-ignore
Overview = withOrientationChange(Overview);

export default Overview;
